import {
  AppShell,
  Box,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';
import { officeCompanies, useWorkplace } from './access';

export function Disclaimer() {
  const { t } = useTranslation('common');
  return (
    <Text size="sm" c="dimmed" component="p">
      {t('disclaimer')}
    </Text>
  );
}

interface NavItem {
  to: string;
  label: string;
}

/** Page frame: header with the company, role-aware navigation, footer with the disclaimer. */
export function Layout() {
  const { t } = useTranslation(['dashboard', 'common']);
  const [opened, { toggle, close }] = useDisclosure();
  const { access, company, vessels, chooseCompany } = useWorkplace();
  const { pathname } = useLocation();
  const companies = officeCompanies(access);

  const work: NavItem[] = [
    { to: '/', label: t('nav.fleet') },
    { to: '/waiting', label: t('nav.waiting') },
    { to: '/weekly', label: t('nav.weekly') },
  ];
  const content: NavItem[] = [
    { to: '/content', label: t('nav.content') },
    { to: '/reassign', label: t('nav.reassign') },
  ];
  const admin: NavItem[] = [
    { to: '/admin/companies', label: t('nav.companies') },
    { to: '/admin/vessels', label: t('nav.vessels') },
    { to: '/admin/office-users', label: t('nav.officeUsers') },
    { to: '/admin/content', label: t('nav.versions') },
  ];
  const profile: NavItem = { to: '/profile', label: access.fullName || access.email };
  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));
  const link = (item: NavItem) => (
    <NavLink
      key={item.to}
      component={Link}
      to={item.to}
      label={item.label}
      active={isActive(item.to)}
      onClick={close}
    />
  );

  return (
    <AppShell
      header={{ height: 64 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label={t('nav.menu')}
            />
            <Text fw={700} size="lg">
              {t('common:appName')}
            </Text>
          </Group>
          {companies.length > 1 ? (
            <Select
              aria-label={t('nav.company')}
              data={companies.map((c) => ({ value: c.id, label: c.name }))}
              value={company.id}
              onChange={(value) => value !== null && chooseCompany(value)}
              allowDeselect={false}
              w={240}
            />
          ) : (
            <Text fw={600}>{company.name}</Text>
          )}
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm" aria-label={t('nav.main')}>
        <ScrollArea>
          {work.map(link)}
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="md" mb={4} px="sm">
            {t('nav.vesselsTitle')}
          </Text>
          {vessels.map((v) => link({ to: `/vessels/${v.id}`, label: v.name }))}
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="md" mb={4} px="sm">
            {t('nav.contentTitle')}
          </Text>
          {content.map(link)}
          {access.isAdmin ? (
            <>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="md" mb={4} px="sm">
                {t('nav.adminTitle')}
              </Text>
              {admin.map(link)}
            </>
          ) : null}
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="md" mb={4} px="sm">
            {t('nav.youTitle')}
          </Text>
          {link(profile)}
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>
        <Stack justify="space-between" mih="calc(100dvh - 64px - 2 * var(--mantine-spacing-lg))">
          <div>
            <Outlet />
          </div>
          <Box component="footer" pt="xl">
            <Disclaimer />
          </Box>
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
}
